import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useItemPrice } from "../config";
import type { DiscoverItem } from "../types";
import { useDiscoverActions, useDiscoverContext } from "../state/discover-context";
import { cn } from "../internal/cn";
import { useRecentBooking } from "../hooks/use-recent-booking";
import { thumbSrcSet, thumbUrl } from "./map/thumb-url";

import { Icon } from "@majourneys/icons";
interface DiscoverCardProps {
  readonly item: DiscoverItem;
}

/**
 * The Discover card in the pewter frame.
 *
 * Styling lives in `styles/discover.css` under `.mj-dcard`, not in Tailwind
 * classes here: the app's Tailwind build scans only `apps/majourneys/src` and
 * `packages/booking/src`, so an arbitrary class written in this package is
 * emitted only when the app happens to use the same string somewhere else.
 * The selected-card ring used to be `ring-[#040033]`, which nothing else in
 * the app writes, so it was never emitted at all.
 */
export function DiscoverCard({ item }: DiscoverCardProps) {
  const { t } = useTranslation();
  const itemPrice = useItemPrice();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  const { state } = useDiscoverContext();
  const { selectItem, toggleLiked } = useDiscoverActions();
  const isBookable = item.mode === "bookable";
  const isSelected = state.selectedItemId === item.id && state.isDetailOpen;
  const isLiked = state.likedIds.has(item.id);
  const recentBookingRef = useRecentBooking(item.sourceSlug);
  const showBookedCta = isBookable && !!recentBookingRef;
  const shortRef =
    recentBookingRef && recentBookingRef.length > 12
      ? `${recentBookingRef.slice(0, 7)}…${recentBookingRef.slice(-4)}`
      : recentBookingRef;

  const open = () => selectItem(item.id);
  const priceLabelKey = getPriceLabelKey(priceKind(item));

  const kindLabel = sentenceCase(item.subtitle, locale);
  const unitLabel = priceLabelKey
    ? t(priceLabelKey === "activity.night" ? "activity.per_night" : priceLabelKey)
    : t("activity.per_person");

  return (
    <article className={cn("mj-dcard", isSelected && "is-selected")} data-item-id={item.id}>
      {/* The pewter frame holds the photo only; the text sits under it on
          the page, like the rift-lab card A the owner picked (2026-09-28). */}
      <div className="mj-dcard__photo mj-dk-frame">
        <button
          type="button"
          onClick={open}
          aria-label={t("discover.open_item", { title: item.title })}
          className="mj-press mj-dcard__media-btn"
        >
          <span className="mj-dcard__media">
            {item.image ? (
              <img
                src={thumbUrl(item.image, 480)}
                srcSet={thumbSrcSet(item.image, [240, 480, 720])}
                sizes="240px"
                alt={item.imageAlt ?? item.title}
                loading="lazy"
              />
            ) : (
              <span className="mj-dcard__placeholder">
                <Icon name="ri-image-line" aria-hidden="true" />
              </span>
            )}
          </span>
        </button>

        {/* Bookable and explore-only places share the same rails; the pill
            tells them apart at a glance. */}
        {isBookable && (
          <span className="mj-dcard__book-pill">{t("discover.card_bookable", { defaultValue: "Bookable" })}</span>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleLiked(item.id);
          }}
          aria-pressed={isLiked}
          aria-label={isLiked ? t("place.unsave") : t("activity.save")}
          className="mj-press mj-dcard__like"
        >
          <Icon name={isLiked ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true" />
        </button>
      </div>

      <div className="mj-dcard__body">
        <h3 className="mj-dcard__title">
          <button type="button" onClick={open} className="mj-dcard__title-btn">
            {item.title}
          </button>
        </h3>
        <p className="mj-dcard__meta">
          {[kindLabel, item.location].filter(Boolean).join(" · ")}
        </p>
        {isBookable && itemPrice(item) && (
          <p className="mj-dcard__price">
            {itemPrice(item)} <span className="mj-dcard__price-unit">{unitLabel}</span>
          </p>
        )}
        {showBookedCta && recentBookingRef && (
          <Link
            to="/$lang/booking/$bookingId/thank-you"
            params={{ lang: locale, bookingId: recentBookingRef }}
            aria-label={t("booking_sheet.view_booking", { defaultValue: "View my booking" })}
            className="mj-press mj-dcard__booked"
          >
            <span className="mj-dcard__booked-left">
              <span className="mj-dcard__booked-tick">
                <Icon name="ri-check-line" aria-hidden />
              </span>
              <span>
                <span className="mj-dcard__booked-eyebrow">
                  {t("activity.timeline_awaiting", { defaultValue: "Awaiting" })}
                </span>
                <span className="mj-dcard__booked-ref">{shortRef}</span>
              </span>
            </span>
            <Icon name="ri-arrow-right-line" className="mj-dcard__booked-go rtl:-scale-x-100" aria-hidden />
          </Link>
        )}
      </div>
    </article>
  );
}

/** "SHOWS" -> "Shows". The segment labels are stored in capitals for the old
 *  badge; under the title they read as a normal word. */
function sentenceCase(label: string | null | undefined, locale: string): string {
  const text = (label ?? "").trim();
  if (!text) return "";
  const lower = text.toLocaleLowerCase(locale);
  return lower.charAt(0).toLocaleUpperCase(locale) + lower.slice(1);
}

/**
 * What the price is per. A hotel's pin label is localized ("Hôtel", "فندق"),
 * so the segment decides for stays rather than the English word match below.
 */
function priceKind(item: DiscoverItem): string | undefined {
  return item.segment === "stays" ? "stay" : (item.pinLabel ?? item.subtitle);
}

function getPriceLabelKey(category?: string): string | undefined {
  const c = (category ?? "").toLowerCase();
  if (/(stay|hotel|riad|room|lodge|camp|villa)/.test(c)) return "activity.night";
  if (/(trip|excursion|workshop|tour|group|boat|pool|jet ski)/.test(c)) return "activity.per_group";
  return undefined;
}
